// 🔧 how_it_works.js
// ✅ Purpose: Public SEO landing page explaining how Let's Go Vote works
// ✅ Features:
// - SEO optimized for event voting software searches
// - Explains event setup process
// - Links to industry pages
// - Links to feature pages
// - Links to Request Event page
// - Uses Helmet metadata
// - Supports future growth and SEO authority

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";

const HowItWorks = () => {
  return (
    <>
      <SeoHead {...PUBLIC_PAGE_SEO.howItWorks} />

      <section id="top-content" className="container-fluid py-5">
        <div className="container">

          <div className="row mb-5">
            <div className="col-lg-8 text-start">
              <div className="text-uppercase text-primary fw-bold mb-2">
                How It Works
              </div>

              <h1 className="display-4 fw-bold mb-3">
                Run your event with QR code voting
              </h1>

              <p className="lead text-muted">
                Let&apos;s Go Vote makes it easy to run public voting,
                judged competitions, silent auctions, volunteer programs,
                and sponsor showcases without paper ballots or complicated software.
              </p>

              <Link
                to="/request_event"
                className="btn btn-primary btn-lg"
              >
                Request Your Event
              </Link>
            </div>
          </div>

          {/* Steps */}

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-4">
              How Let&apos;s Go Vote Works
            </h2>

            <div className="row g-4">

              <div className="col-md-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <h3 className="h5">
                    1️⃣ Create Your Event
                  </h3>

                  <p className="mb-0">
                    Create categories, voting settings,
                    sponsors, volunteers, auctions,
                    and event details.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <h3 className="h5">
                    2️⃣ Add Entries
                  </h3>

                  <p className="mb-0">
                    Add vehicles, artwork, pets,
                    candidates, auction items,
                    or anything guests will interact with.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <h3 className="h5">
                    3️⃣ Print QR Codes
                  </h3>

                  <p className="mb-0">
                    Generate QR codes for entries,
                    ballots, auctions,
                    and event information.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <h3 className="h5">
                    4️⃣ Review Results
                  </h3>

                  <p className="mb-0">
                    Track votes, rankings,
                    scorecards, bids,
                    volunteers, and sponsors.
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* Industries */}

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-3">
              Built For These Events
            </h2>

            <div className="row g-3">

              {[
                {
                  title: "Car Shows",
                  path: "/car-shows",
                },
                {
                  title: "Art Shows",
                  path: "/art-shows",
                },
                {
                  title: "Pet Shows",
                  path: "/pet-shows",
                },
                {
                  title: "School Elections",
                  path: "/school-elections",
                },
                {
                  title: "Silent Auctions",
                  path: "/silent-auctions",
                },
              ].map((item) => (
                <div
                  key={item.path}
                  className="col-md-6 col-lg-4"
                >
                  <Link
                    to={item.path}
                    className="border rounded p-3 d-block text-decoration-none"
                  >
                    {item.title}
                  </Link>
                </div>
              ))}

            </div>
          </div>

          {/* Features */}

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-3">
              Popular Features
            </h2>

            <div className="row g-3">

              {[
                {
                  title: "QR Code Voting",
                  path: "/qr-code-voting",
                },
                {
                  title: "People's Choice Voting",
                  path: "/peoples-choice-voting",
                },
                {
                  title: "Ranked Choice Voting",
                  path: "/ranked-choice-voting",
                },
                {
                  title: "Judged Scorecards",
                  path: "/judged-scorecards",
                },
                {
                  title: "Volunteer Management",
                  path: "/volunteer-management",
                },
                {
                  title: "Sponsor Management",
                  path: "/sponsor-management",
                },
              ].map((item) => (
                <div
                  key={item.path}
                  className="col-md-6 col-lg-4"
                >
                  <Link
                    to={item.path}
                    className="border rounded p-3 d-block text-decoration-none"
                  >
                    {item.title}
                  </Link>
                </div>
              ))}

            </div>
          </div>

          <div className="alert alert-info">
            <h4>
              Ready to get started?
            </h4>

            <p>
              Request your event and we&apos;ll help configure
              the right voting experience.
            </p>

            <Link
              to="/request_event"
              className="btn btn-primary"
            >
              Request Your Event
            </Link>
          </div>

        </div>
      </section>
    </>
  );
};

export default HowItWorks;
